// course-view.component.ts
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, inject, Input, Output, SecurityContext, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { JournalService } from '../services/journal.service';
import { IJournal, IJournalResponse, IJournalSection } from '../interface/ijournal';
import { InputType } from '@core/enums/input-type';
import { ToastService } from '@core/services/toast.service';
import { NzModalService } from 'ng-zorro-antd/modal'
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AuthService } from '@core/services/auth.service';
import { environment } from '@env/environment';

@Component({
  selector: 'prism-course-view',
  templateUrl: './course-view.component.html',
  styleUrl: './course-view.component.scss'
})
export class CourseViewComponent {
  private sanitizer = inject(DomSanitizer);
  private http = inject(HttpClient);
  private journalService = inject(JournalService);
  private fb = inject(FormBuilder);
  private toastService = inject(ToastService);
  private modalService = inject(NzModalService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();
  @Input() courseName?: string = 'course1';
  @Input() courseId: number = 1;
  // Completed course: notebook entries are shown but can't be added, edited, or deleted.
  @Input() readOnly = false;
  @Input() schoolYearId?: number;
  @Output() allSectionsComplete = new EventEmitter<boolean>();
  @Output() courseCompleted = new EventEmitter<boolean>();
  @Output() courseStarted = new EventEmitter<number>();
  @Output() modalVisibleChange = new EventEmitter<boolean>();
  isVisible = false;
  blobUrl?: SafeResourceUrl;
  @ViewChild('course-iframe') iframeRef?: ElementRef<HTMLIFrameElement>;

  journalForm!: FormGroup;
  sections: IJournalSection[] = [];
  journalEntries: IJournalResponse[] = [];
  activePanel: number | null = null;
  InputType = InputType;
  editingEntryId: number | null = null;
  hasCourseStarted: boolean = false;
  courseStartTime: number | null = null;

  // Timeout settings
  courseTimeoutMinutes: number = environment.courseTimeoutMinutes || 60; // total time when course can be completed in minutes
  courseTimeout: number = this.courseTimeoutMinutes * 60 * 1000; //converts courseTimeoutMinutes to milliseconds

  // idleTimeoutMinutes: number = 120; // total time when user is considered idle in minutes
  // idleTimeout: number = this.idleTimeoutMinutes * 60; // converts idleTimeoutMinutes to seconds

  get entry() {
    return this.journalForm.get('entry');
  }

  ngOnInit(): void {
    this.blobUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`assets/courses/course${this.courseId}/index.html`);
    this.journalForm = this.fb.group({
      entry: [null, [Validators.required]]
    });

    this.loadData();

    const iframe = document.getElementById('course-iframe') as HTMLIFrameElement;
    if (iframe) {
      iframe.addEventListener('load', () => {
        this.startMonitoringCourseProgress();
      });
    }
  }

  loadData(): void {
    this.journalService.getJournalSections(this.courseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sections) => {
        this.sections = sections.map(section => ({
          ...section,
          showEntryForm: false
        }));
        this.cdr.markForCheck();
      });

    this.journalService.getByCourseId(this.courseId, this.schoolYearId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((entries) => {
        this.journalEntries = Array.isArray(entries) ? entries : [];
        this.journalEntries = this.journalEntries.map(entry => ({
          ...entry,
          dateSubmitted: new Date(entry.dateSubmitted + 'Z')  // Converts to local time
        }));
        this.cdr.markForCheck();
      });
  }

  calculateProgress(): { completed: number, total: number } {
    const total = this.sections.length;
    const journalEntries = Array.isArray(this.journalEntries) ? this.journalEntries : [];

    const completed = this.sections.filter(section =>
      journalEntries.some(entry => entry.journalSectionId === section.id)
    ).length;
    // Emit event when all sections are complete
    const isAllComplete = completed === total;
    this.allSectionsComplete.emit(isAllComplete);

    return { completed, total };
  }

  isSectionComplete(sectionId: number): boolean {
    return this.journalEntries.some(entry => entry.journalSectionId === sectionId);
  }

  showModal(): void {
    this.loadData();
    this.isVisible = true;
    this.modalVisibleChange.emit(true);
  }

  handleCancel(): void {
    this.isVisible = false;
    this.modalVisibleChange.emit(false);
  }

  editEntry(event: Event, entry: IJournalResponse): void {
    event.stopPropagation();
    this.editingEntryId = entry.id;
    this.sections = this.sections.map(section => ({
      ...section,
      showEntryForm: section.id === entry.journalSectionId
    }));
    this.activePanel = entry.journalSectionId;
    this.journalForm.patchValue({
      entry: entry.entry
    });
  }

  showEntryForm(event: Event, sectionId: number): void {
    event.stopPropagation();
    this.sections = this.sections.map(section => ({
      ...section,
      showEntryForm: section.id === sectionId
    }));
    this.activePanel = sectionId;
    this.journalForm.reset();
  }

  cancelEntry(event: Event): void {
    event.stopPropagation();
    this.sections = this.sections.map(section => ({
      ...section,
      showEntryForm: false
    }));
    this.editingEntryId = null;
    this.journalForm.reset();
  }

  submitJournalEntry(sectionId: number, minLength: number): void {
    const entryText = this.journalForm.get('entry')?.value || '';

    if (entryText.length < minLength) {
      this.toastService.error(`Notebook Entry does not meet the minimum characters (${minLength} characters)`);
      return;
    }
    if (this.journalForm.valid) {
      const payload: IJournal = {
        courseId: this.courseId,
        entry: this.journalForm.value.entry,
        journalSectionId: sectionId
      };

      this.journalService.addJournal(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loadData();
          this.editingEntryId = null;
          this.sections = this.sections.map(section => ({
            ...section,
            showEntryForm: false
          }));
          this.journalForm.reset();
        });
    }
  }

  updateJournalEntry(sectionId: number, minLength: number): void {
    if (this.journalForm.valid && this.editingEntryId) {
      if (this.journalForm.value.entry.length < minLength) {
        this.toastService.error(`Notebook Entry does not meet the minimum characters (${minLength} characters)`);
        return;
      }
      const payload: IJournal & { id: number } = {
        id: this.editingEntryId,
        courseId: this.courseId,
        entry: this.journalForm.value.entry,
        journalSectionId: sectionId
      };

      this.journalService.editJournal(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.loadData();
          this.editingEntryId = null;
          this.journalForm.reset();
        })
    }
  }

  deleteEntry(event: Event): void {
    event.stopPropagation();
    let journalEntryId = this.editingEntryId || 0;

    if (!journalEntryId) {
      this.toastService.error('Notebook entry not found');
      return;
    }

    this.modalService.confirm({
      nzTitle: `Delete Entry`,
      nzContent: 'Are you sure you want to delete this entry?',
      nzOkText: 'Yes',
      nzIconType: 'exclamation-circle',
      nzCancelText: 'No',
      nzOnOk: () => {
        this.journalService.deleteJournal(journalEntryId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res: boolean) => {
              if (res) {
                this.toastService.success('Entry deleted successfully');
                this.editingEntryId = null;
                this.sections = this.sections.map(section => ({
                  ...section,
                  showEntryForm: false
                }));
                this.journalForm.reset();
                this.loadData();
              }
            },
            error: () => {
              this.toastService.error('Error deleting entry');
            }
          });
      },
    });
  }

  startMonitoringCourseProgress() {
    const iframe = document.getElementById('course-iframe') as HTMLIFrameElement;

    if (!iframe) {
      this.toastService.error("There was an issue loading the course. Please refresh the page and try again.");
      return;
    }

    const intervalId = setInterval(() => {
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      const progressText = iframeDocument?.querySelector('.nav-sidebar-header__progress-text')?.innerHTML?.trim();
      if (this.hasCourseStarted === false && progressText === '0% COMPLETE') {
        this.hasCourseStarted = true;
        this.courseStartTime = Date.now();
        this.courseStarted.emit(this.courseStartTime);
      }

      // Check if courseTimeoutMinutes have elapsed
      const currentTime = Date.now();
      const hasTimedOut = this.courseStartTime && (currentTime - this.courseStartTime) >= this.courseTimeout;

      // Complete course if 100% complete and courseTimeoutMinutes have passed
      if (hasTimedOut && progressText === '100% COMPLETE') {
        clearInterval(intervalId);
        this.courseCompleted.emit(true);
      }
    }, 3000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
